export enum CurriculumArea {
  Physical,
  Linguistic,
  Cognitive,
  SocialEmotional,
}

export enum CurriculumYear {
  Reception,
  Year1,
  Year2,
  Year3,
  Year4,
  Year5,
  Year6,
}

export interface CurricilumCriteria {
  key: string;
  description: string;
  area: CurriculumArea;
  year: CurriculumYear;
  grade?: number;
}

export const criteria: CurricilumCriteria[] = [
  {
    key: 'physical_reception_0',
    description: 'To speak audibly so they can be heard and understood',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Reception,
  },
  {
    key: 'physical_reception_1',
    description: 'To use gestures to support meaning in play',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Reception,
  },

  {
    key: 'linguistic_reception_0',
    description: 'To use talk in play to practice new vocabulary',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Reception,
  },
  {
    key: 'linguistic_reception_1',
    description: 'To join phrases with words such as ‘if’, ‘because’ ‘so’ ‘could’ ‘but’',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Reception,
  },

  {
    key: 'cognitive_reception_0',
    description: 'To use ‘because’ to develop their ideas',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Reception,
  },
  {
    key: 'cognitive_reception_1',
    description: 'To make relevant contributions and asks questions',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Reception,
  },
  {
    key: 'cognitive_reception_2',
    description: 'To describe events that have happened to them in detail',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Reception,
  },

  {
    key: 'social_reception_0',
    description: 'To look at someone who is speaking to them',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Reception,
  },
  {
    key: 'social_reception_1',
    description: 'To take turns to speak when working in a group',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Reception,
  },

  {
    key: 'physical_year1_0',
    description:
      'To use the appropriate tone of voice in the right context. E.g. speaking calmly when resolving an issue in the playground.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year1,
  },
  {
    key: 'physical_year1_1',
    description: 'To speak clearly and confidently in a range of contexts',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year1,
  },

  {
    key: 'linguistic_year1_0',
    description: 'To use vocabulary appropriate specific to the topic at hand',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year1,
  },
  {
    key: 'linguistic_year1_1',
    description: 'To take opportunities to try out new language, even if not always used correctly.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year1,
  },
  {
    key: 'linguistic_year1_2',
    description:
      'To use sentence stems to link to other’s ideas in group discussion. E.g. ‘I agree with… because …’ ‘Linking to …’',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year1,
  },
  {
    key: 'linguistic_year1_3',
    description: 'To use conjunctions to organise and sequence ideas e.g. firstly, secondly, finally.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year1,
  },

  {
    key: 'cognitive_year1_0',
    description: 'To offer reasons for their opinions',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year1,
  },
  {
    key: 'cognitive_year1_1',
    description: 'To recognise when they haven’t understood something and asks a question to help with this.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year1,
  },
  {
    key: 'cognitive_year1_2',
    description: 'To disagree with someone else’s opinion politely.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year1,
  },
  {
    key: 'cognitive_year1_3',
    description: 'To explain ideas and events in chronological order.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year1,
  },

  {
    key: 'social_year1_0',
    description: 'Listens to others and is willing to change their mind based on what they have heard',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year1,
  },
  {
    key: 'social_year1_1',
    description: 'To organise group discussions independently of an adult.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year1,
  },

  {
    key: 'social_year2_0',
    description:
      'To start to use gesture to support the delivery of ideas e.g. gesturing towards someone if referencing their idea, or counting off ideas on their fingers as they say them.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year2,
  },

  {
    key: 'linguistic_year2_0',
    description: 'To adapt how they speak in different situations according to audience.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year2,
  },
  {
    key: 'linguistic_year2_1',
    description: 'To use sentence stems to signal when they are building on or challenging others’ ideas.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year2,
  },

  {
    key: 'cognitive_year2_0',
    description: 'To ask questions to find out more about a subject.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year2,
  },
  {
    key: 'cognitive_year2_1',
    description: 'To build on others’ ideas in discussions.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year2,
  },
  {
    key: 'cognitive_year2_2',
    description: 'To make connections between what has been said and their own and others’ experiences.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year2,
  },

  {
    key: 'emotional_year2_0',
    description: 'To start to develop an awareness of audience e.g. what might interest a certain group.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year2,
  },
  {
    key: 'emotional_year2_1',
    description: 'To be aware of others who have not spoken and to invite them into discussion.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year2,
  },
  {
    key: 'emotional_year2_2',
    description: 'Confident delivery of short pre-prepared material.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year2,
  },

  {
    key: 'physical_year3_0',
    description:
      'Deliberately varies tone of voice in order to convey meaning. E.g. speaking authoritatively during an expert talk or speaking with pathos when telling a sad part of a story.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year3,
  },
  {
    key: 'physical_year3_1',
    description: 'Considers position and posture when addressing an audience.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year3,
  },

  {
    key: 'linguistic_year3_0',
    description: 'To be able to use specialist language to describe their own and others’ talk.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year3,
  },
  {
    key: 'linguistic_year3_1',
    description: 'To use specialist vocabulary. ',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year3,
  },
  {
    key: 'linguistic_year3_2',
    description: 'To make precise language choices e.g. instead of describing a cake as ‘nice’ using ‘delectable’.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year3,
  },

  {
    key: 'cognitive_year3_0',
    description: 'To offer opinions that aren’t their own.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year3,
  },
  {
    key: 'cognitive_year3_1',
    description: 'To reflect on discussions and identify how to improve.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year3,
  },
  {
    key: 'cognitive_year3_2',
    description: 'To be able to summarise a discussion.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year3,
  },
  {
    key: 'cognitive_year3_3',
    description: 'To reach shared agreement in discussions.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year3,
  },

  {
    key: 'social_year3_0',
    description: 'To adapt the content of their speech for a specific audience.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year3,
  },
  {
    key: 'social_year3_1',
    description: 'To speak with confidence in front of an audience.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year3,
  },

  {
    key: 'physical_year4_0',
    description: 'To consider movement when addressing an audience.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year4,
  },
  {
    key: 'physical_year4_1',
    description: 'To use pauses for effect in presentational talk e.g. when telling a anecdote or telling a joke.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year4,
  },

  {
    key: 'linguistic_year4_0',
    description:
      'To carefully consider the words and phrasing they use to express their ideas and how this supports the purpose of talk.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year4,
  },

  {
    key: 'cognitive_year4_0',
    description: 'To be able to give supporting evidence e.g. citing a text, a previous example or a historical event.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year4,
  },
  {
    key: 'cognitive_year4_1',
    description: 'To ask probing questions.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year4,
  },
  {
    key: 'cognitive_year4_2',
    description: 'To reflect on their own oracy skills and identify areas of strength and areas to improve.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year4,
  },

  {
    key: 'social_year4_0',
    description: 'To use more natural and subtle prompts for turn taking.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year4,
  },
  {
    key: 'social_year4_1',
    description: 'To be able to empathise with an audience.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year4,
  },
  {
    key: 'social_year4_2',
    description: 'To consider the impact of their words on others when giving feedback.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year4,
  },

  {
    key: 'physical_year5_0',
    description: 'To project their voice to large audience.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year5,
  },
  {
    key: 'physical_year5_1',
    description: 'For gestures to become increasingly natural. ',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year5,
  },

  {
    key: 'linguistic_year5_0',
    description: 'To use an increasingly sophisticated range of sentence stems with fluency and accuracy.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year5,
  },

  {
    key: 'cognitive_year5_0',
    description:
      'To be able to draw upon knowledge of the world to support their own point of view and explore different perspectives. E.g. In a discussion about vegetarianism, rather than saying ‘my mum is a vegetarian so eating meat is wrong’ to be able to say ‘lots of people don’t eat meat because they believe killing animals is cruel’. ',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year5,
  },
  {
    key: 'cognitive_year5_1',
    description: 'To identify when a discussion is going off topic and to be able to bring it back on track.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year5,
  },

  {
    key: 'social_year5_0',
    description: 'Listening for extended periods of time.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year5,
  },
  {
    key: 'social_year5_1',
    description: 'To speak with flair and passion.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year5,
  },

  {
    key: 'physical_year6_0',
    description: 'To speak fluently in front of an audience.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year6,
  },
  {
    key: 'physical_year6_1',
    description: 'To have a stage presence.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year6,
  },
  {
    key: 'physical_year6_2',
    description: 'Consciously adapt tone, pace and volume of voice within a single situation.',
    area: CurriculumArea.Physical,
    year: CurriculumYear.Year6,
  },

  {
    key: 'linguistic_year6_0',
    description: 'To vary sentence structures and length for effect when speaking.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year6,
  },
  {
    key: 'linguistic_year6_1',
    description: 'To be comfortable using idiom and expressions.',
    area: CurriculumArea.Linguistic,
    year: CurriculumYear.Year6,
  },

  {
    key: 'cognitive_year6_0',
    description: 'To construct a detailed argument or complex narrative.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year6,
  },
  {
    key: 'cognitive_year6_1',
    description: 'To spontaneously respond to increasingly complex questions, citing evidence where appropriate.',
    area: CurriculumArea.Cognitive,
    year: CurriculumYear.Year6,
  },

  {
    key: 'social_year6_0',
    description: 'To use humour effectively.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year6,
  },
  {
    key: 'social_year6_1',
    description:
      'To be able to read a room or a group and take action accordingly e.g. if everyone looks disengaged, moving on or changing topic, or if people look confused stopping to take questions.',
    area: CurriculumArea.SocialEmotional,
    year: CurriculumYear.Year6,
  },
];
